/**
 * /test/mobile-shell-demo — Phase 1 proof page.
 *
 * Mounts every canonical mobile primitive so a human + Playwright can
 * verify the Contract is satisfied. NOT linked from any navigation —
 * direct-URL only. Lives under /test/* so it is obvious this is QA
 * scaffolding, not user-facing.
 *
 * Phase 1 deliverable. May 14, 2026.
 */
import { useState } from "react";
import {
  MobilePageShell,
  MobileDialog,
  MobileBottomSheet,
  MobileMenu,
  MobilePopover,
} from "../components/mobile";
import { Settings, ChevronDown, Info } from "lucide-react";

export default function MobileShellDemo() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <MobilePageShell
      testid="demo-page-shell"
      bottomNavOffset="none"
      className="bg-[#050505] text-white"
    >
      <div className="max-w-md mx-auto px-5 py-10 space-y-6">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Mobile Shell Demo</h1>
          <p className="text-[13px] text-zinc-400 mt-1">
            Phase 1 proof page. Each primitive is mounted exactly once.
          </p>
        </header>

        {/* MobileDialog */}
        <section className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <h2 className="text-[13px] font-bold mb-2">MobileDialog</h2>
          <button
            data-testid="demo-open-dialog"
            onClick={() => setDialogOpen(true)}
            className="h-10 px-4 rounded-lg border border-white/15 hover:border-white/30 text-[12.5px] font-bold"
          >
            Open dialog
          </button>
        </section>

        {/* MobileBottomSheet */}
        <section className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <h2 className="text-[13px] font-bold mb-2">MobileBottomSheet</h2>
          <button
            data-testid="demo-open-sheet"
            onClick={() => setSheetOpen(true)}
            className="h-10 px-4 rounded-lg border border-white/15 hover:border-white/30 text-[12.5px] font-bold"
          >
            Open bottom sheet
          </button>
        </section>

        {/* MobileMenu */}
        <section className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <h2 className="text-[13px] font-bold mb-2">MobileMenu</h2>
          <MobileMenu>
            <MobileMenu.Trigger asChild>
              <button
                data-testid="demo-open-menu"
                className="h-10 px-4 rounded-lg border border-white/15 hover:border-white/30 text-[12.5px] font-bold inline-flex items-center gap-2"
              >
                <Settings className="w-4 h-4" /> Menu <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </MobileMenu.Trigger>
            <MobileMenu.Content testid="demo-menu">
              <MobileMenu.Label>Actions</MobileMenu.Label>
              <MobileMenu.Item testid="demo-menu-edit">Edit</MobileMenu.Item>
              <MobileMenu.Item testid="demo-menu-duplicate">Duplicate</MobileMenu.Item>
              <MobileMenu.Separator />
              <MobileMenu.Item testid="demo-menu-delete" destructive>Delete</MobileMenu.Item>
            </MobileMenu.Content>
          </MobileMenu>
        </section>

        {/* MobilePopover */}
        <section className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <h2 className="text-[13px] font-bold mb-2">MobilePopover</h2>
          <MobilePopover>
            <MobilePopover.Trigger asChild>
              <button
                data-testid="demo-open-popover"
                className="h-10 px-4 rounded-lg border border-white/15 hover:border-white/30 text-[12.5px] font-bold inline-flex items-center gap-2"
              >
                <Info className="w-4 h-4" /> Info
              </button>
            </MobilePopover.Trigger>
            <MobilePopover.Content testid="demo-popover">
              <p className="text-[12.5px] text-zinc-300">
                Popovers auto-flip + shift on small viewports. Tap outside or
                press ESC to dismiss. Body never overflows the screen.
              </p>
            </MobilePopover.Content>
          </MobilePopover>
        </section>
      </div>

      <MobileDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        testid="demo-dialog"
        title="Demo dialog"
        description="Bottom-sheet snap on mobile, centred on ≥sm."
        footer={
          <>
            <button
              data-testid="demo-dialog-cancel"
              onClick={() => setDialogOpen(false)}
              className="h-10 px-4 rounded-lg border border-white/15 text-[12.5px] font-bold"
            >
              Cancel
            </button>
            <button
              data-testid="demo-dialog-confirm"
              onClick={() => setDialogOpen(false)}
              className="h-10 px-4 rounded-lg bg-white text-black text-[12.5px] font-bold"
            >
              Confirm
            </button>
          </>
        }
      >
        <p className="text-[13px] text-zinc-300 leading-relaxed">
          The body region is internally scrollable. Try resizing — the dialog
          never overflows the viewport, the X button is always reachable, and
          the safe-area inset is respected at the bottom.
        </p>
        <div className="mt-3 space-y-2">
          {Array.from({ length: 18 }).map((_, i) => (
            <div
              key={i}
              className="rounded-md bg-white/[0.04] border border-white/[0.06] px-3 py-2 text-[12px] text-zinc-400"
            >
              Filler line {i + 1} — proving internal scroll
            </div>
          ))}
        </div>
      </MobileDialog>

      <MobileBottomSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        testid="demo-sheet"
        title="Demo bottom sheet"
        description="Always bottom-anchored on mobile."
        footer={
          <button
            data-testid="demo-sheet-done"
            onClick={() => setSheetOpen(false)}
            className="h-10 px-4 rounded-lg bg-white text-black text-[12.5px] font-bold"
          >
            Done
          </button>
        }
      >
        <p className="text-[13px] text-zinc-300 leading-relaxed">
          Bottom sheets sit over the Active Operation pill via z-[1100] and
          respect <code className="font-mono">env(safe-area-inset-bottom)</code>{" "}
          so the home indicator never overlaps the primary CTA.
        </p>
      </MobileBottomSheet>
    </MobilePageShell>
  );
}
